import {
  activeSignerTypes,
  ESIGN_PAGE_WIDTH,
  getRoleVariant,
  getSignerConfig,
  getSignerTitle,
  signerTypeNeedsValue,
} from "@/pages/esign/esign.config.ts";
import { EsignSignerInput, EsignSignerModal } from "@/pages/esign/widgets/EsignSignerModal.tsx";
import { ApiEsignMarkupRole, EsignConditionType } from "@/types/esign.types.ts";
import {
  Box,
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  FontAwesomeIcon,
  Input,
  Stack,
  Tag,
  Text,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

export interface EsignDocumentCardProps {
  fileName: string;
  title: string;
  onTitleChange: (title: string) => void;
  onTitleCommit: () => void;
  roles: ApiEsignMarkupRole[];
  onAddSigner: (input: EsignSignerInput) => void;
  onUpdateSigner: (index: number, input: EsignSignerInput) => void;
  onDeleteSigner: (index: number) => void;
}

export function EsignDocumentCard({
  fileName,
  title,
  roles,
  onAddSigner,
  onUpdateSigner,
  onTitleChange,
  onTitleCommit,
  onDeleteSigner,
}: EsignDocumentCardProps) {
  const { t } = useTranslation();
  const { openModal } = useModals();

  const usedNonValueTypes = new Set(
    roles.filter((role) => !signerTypeNeedsValue(role.condition_type)).map((r) => r.condition_type)
  );

  const isTypeSelectable = (type: EsignConditionType, currentType?: EsignConditionType) =>
    type === currentType || signerTypeNeedsValue(type) || !usedNonValueTypes.has(type);

  const pickType = (type: EsignConditionType, index?: number) => {
    if (signerTypeNeedsValue(type)) {
      openModal(EsignSignerModal, {
        conditionType: type,
        role: index !== undefined ? roles[index] : undefined,
        onSubmit: (input: EsignSignerInput) => {
          if (index !== undefined) onUpdateSigner(index, input);
          else onAddSigner(input);
        },
      });
      return;
    }

    const input: EsignSignerInput = {
      condition_type: type,
      condition_value: [],
      condition_entities: [],
    };
    if (index !== undefined) onUpdateSigner(index, input);
    else onAddSigner(input);
  };

  const renderTypeItems = (currentType?: EsignConditionType, index?: number): ReactNode[] => {
    const selectable = activeSignerTypes.filter((type) => isTypeSelectable(type, currentType));
    const simpleTypes = selectable.filter((type) => !signerTypeNeedsValue(type));
    const valueTypes = selectable.filter((type) => signerTypeNeedsValue(type));

    const items: ReactNode[] = [];

    simpleTypes.forEach((type) => {
      items.push(
        <DropdownMenuItem
          key={type}
          title={t(getSignerConfig(type).labelKey)}
          isSelected={type === currentType}
          onClick={() => {
            pickType(type, index);
          }}
        />
      );
    });

    if (simpleTypes.length > 0 && valueTypes.length > 0) {
      items.push(<DropdownMenuSeparator key={"sep-value"} />);
    }

    valueTypes.forEach((type) => {
      items.push(
        <DropdownMenuItem
          key={type}
          isSelected={type === currentType}
          title={
            <Stack
              direction={"horizontal"}
              items={"center"}
              justify={"between"}
              className={"w-full gap-6"}
            >
              {t(getSignerConfig(type).labelKey)}
              <FontAwesomeIcon icon={"fa-light fa-chevron-right"} className={"text-secondary"} />
            </Stack>
          }
          onClick={() => {
            pickType(type, index);
          }}
        />
      );
    });

    return items;
  };

  return (
    <Box
      className={"mx-auto w-full rounded-xs bg-light p-8 shadow-sm"}
      style={{ maxWidth: ESIGN_PAGE_WIDTH }}
    >
      <Stack direction={"horizontal"} justify={"between"} items={"center"}>
        <Title header={fileName} size="xs" />
      </Stack>

      <Stack className={"gap-1.5 pt-8"}>
        <Input
          value={title}
          label={t("common:title")}
          onChange={(e) => {
            onTitleChange(e.target.value);
          }}
          onBlur={() => {
            onTitleCommit();
          }}
          size="lg"
        />
      </Stack>

      <Box className={"pt-10"}>
        <Text className={"title-sub-headline pb-2 text-secondary"}>{t("core:esign_signers")}</Text>
        <Stack direction={"horizontal"} items={"center"} gap={"sm"} className={"flex-wrap"}>
          {roles.map((role, index) => {
            const label = getSignerTitle(role, t);

            if (index === 0) {
              return (
                <Tag
                  key={`signer-${index}`}
                  variant={getRoleVariant(index)}
                  appearance="filled"
                  className="text-light"
                >
                  <Stack gap="xs" items="center" direction="horizontal">
                    <FontAwesomeIcon icon="fa-light fa-signature" />
                    <Text>{label}</Text>
                  </Stack>
                </Tag>
              );
            }

            return (
              <DropdownMenu
                key={`signer-${index}`}
                trigger={
                  <Tag variant={getRoleVariant(index)} appearance="filled" className="text-light">
                    <Stack gap="xs" items="center" direction="horizontal">
                      <FontAwesomeIcon icon="fa-light fa-signature" />
                      <Text>{label}</Text>
                      <FontAwesomeIcon icon="fa-light fa-chevron-down" />
                    </Stack>
                  </Tag>
                }
              >
                {renderTypeItems(role.condition_type, index)}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  title={t("core:esign_delete_signer")}
                  variant={"menuDanger"}
                  leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                  onClick={() => {
                    onDeleteSigner(index);
                  }}
                />
              </DropdownMenu>
            );
          })}

          <DropdownMenu
            trigger={
              <ButtonIcon
                variant={"secondary"}
                size="sm"
                icon={<FontAwesomeIcon icon={"fa-light fa-plus"} />}
              />
            }
          >
            {renderTypeItems()}
          </DropdownMenu>
        </Stack>
      </Box>
    </Box>
  );
}
